import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button, Card } from "@atlas/ui/web";
import { atlas } from "../../lib/sdk";
import { readActiveMatricula, STORAGE_KEY_META, STORAGE_KEY_ID, type MatriculaInfo } from "../../lib/matricula-data";



const pct = (n: number) => `${(n * 100).toFixed(2)}% a.m.`;

/** Deriva label/cor/CTA/href do card de oferta a partir do tipo. Centraliza o
 *  roteamento pra manter o JSX enxuto e o comportamento consistente entre esta
 *  pagina e /servidor/marketplace. */
function tipoLabelHref(
  o: {
    id: string;
    tipo: "credito_novo" | "portabilidade" | "refinanciamento" | "cartao_consignado" | "cartao_beneficio";
    bancoNome: string;
    taxaAm: number; // FRACAO (0.0179 = 1.79% a.m.)
    valorMax: number;
    parcelasMax: number;
  },
  valorSug: number,
  parcelasSug: number,
): { label: string; cor: string; cta: string; href: string } {
  if (o.tipo === "portabilidade") {
    return {
      label: "🔁 Portabilidade",
      cor: "var(--gold-500)",
      cta: "Levar meu contrato →",
      href: `/servidor/portabilidade?banco=${encodeURIComponent(o.bancoNome)}`,
    };
  }
  if (o.tipo === "refinanciamento") {
    return {
      label: "🔄 Refinanciamento",
      cor: "var(--accent)",
      cta: "Refinanciar contrato →",
      href: `/servidor/refinanciamento?banco=${encodeURIComponent(o.bancoNome)}`,
    };
  }
  if (o.tipo === "cartao_consignado" || o.tipo === "cartao_beneficio") {
    const label = o.tipo === "cartao_consignado" ? "💳 Cartão consignado" : "🎫 Cartão benefício";
    return {
      label,
      cor: "var(--gold-500)",
      cta: "Solicitar cartão →",
      // Fluxo proprio de cartao — mostra margem cartao correspondente + limite
      // proposto e faz POST /me/cartoes ao confirmar.
      href: `/servidor/solicitar-cartao?produto=${o.tipo}&banco=${encodeURIComponent(o.bancoNome)}&limite=${Math.round(o.valorMax)}&oferta=${encodeURIComponent(o.id)}`,
    };
  }
  return {
    label: "💰 Crédito novo",
    cor: "var(--emerald-500)",
    cta: "Simular oferta →",
    // Cliente pediu 29/07/2026: aceitar oferta de credito novo NAO pode ir
    // direto pro Termo com valor/parcelas fixos — leva pro simulador com o
    // teto/parcelas maximas da oferta pre-preenchidos pra o servidor ajustar
    // valor e parcela antes de solicitar. A taxa vem da oferta vencedora
    // do backend (SimuladorInline puxa do useQuery de ofertas ativas).
    //
    // Cliente pediu 30/07/2026: passar oferta/maxParcelas/maxValor/taxa como
    // HARD CAP no simulador (INDEPENDENTE do prazoMax e taxa da tabela geral
    // do banco). Backend re-valida ofertaId contra a oferta ativa. taxa vai
    // em PERCENT (0.0179 -> 1.79) que e o formato que /simular parseia.
    // `banco` identifica de QUEM e a promocao. Sem ele o simulador escolhia o
    // banco pela menor taxa entre TODAS as tabelas da prefeitura — o servidor
    // clicava na oferta do banco A e a proposta saia pro banco B, que nunca
    // fez promocao nenhuma. Ver ofertaVencedora em _simulador-inline.tsx.
    href: `/servidor/simular?produto=emprestimo&valor=${valorSug}&parcelas=${parcelasSug}&oferta=${encodeURIComponent(o.id)}&banco=${encodeURIComponent(o.bancoNome)}&maxParcelas=${o.parcelasMax}&maxValor=${o.valorMax}&taxa=${(o.taxaAm * 100).toFixed(4)}`,
  };
}

// O RESÍDUO DA SEÇÃO DE PROPOSTAS SAIU DAQUI EM 08/09/2026 — item 4 do
// PENDENCIAS.md, e vale registrar por que apagar não escondeu defeito nenhum.
//
// O item descrevia "o filtro por aba desta tela" como tela inacabada: `setTab`
// nunca chamado, `filtradas` nunca renderizado. Apurado, era o oposto — o filtro
// não estava por terminar, estava sobrando. A seção que ele filtrava foi
// REMOVIDA A PEDIDO DO CLIENTE em 27/07/2026 ("só quero que apareça (Ofertas
// para você e Solicitar Portabilidade)"), e os componentes que a desenhavam —
// `FeaturedComTrocoCard`, `SimplesCard`, `SubCard`, `TabBtn`, `pillStyle` —
// saíram na mesma data. Ligar o filtro não seria ligar coisa nenhuma: seria
// reescrever a UI que o cliente mandou tirar.
//
// Saiu junto o que só existia para alimentá-la: o tipo `Proposta`, `Tab`,
// `ehPortabilidade`, `temTroco`, `trocoValor`, `parseBrDate`, `diasRestantes`,
// `fmtDataBr`, e — o que de fato custava — a query `propostas`, que batia na API
// A CADA 15 SEGUNDOS, mais um `setInterval` de um minuto que só existia para
// atualizar o countdown daqueles cards. Trabalho de rede e de render por uma
// tela que ninguém via.
//
// As propostas de portabilidade continuam chegando em `/servidor/portabilidade`,
// que é onde o cliente pediu que ficassem.

export function ServidorMarketplacePortabilidade() {
  const nav = useNavigate();
  const [info, setInfo] = useState<MatriculaInfo | null>(() => readActiveMatricula());

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY_META || e.key === STORAGE_KEY_ID) {
        setInfo(readActiveMatricula());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const matAtiva = info?.matricula;

  // Ofertas: SO ofertas que o banco criou e publicou manualmente (marketing/
  // campanha). NAO as tabelas de emprestimo automaticas — o cliente disse:
  // "esse espaco e apenas para ofertas que o banco criar e publicar".
  // Backend ja filtra por perfil (convenio+prefeitura+vinculo+situacao+salario).
  const ofertasQ = useQuery({
    queryKey: ["servidor", "ofertas-banco", matAtiva],
    queryFn: () => atlas.servidor.getMyOfertasBanco(matAtiva),
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    enabled: !!matAtiva,
  });

  // Cartao Beneficio nao entra no app (decisao cliente 14/07/2026). A oferta de
  // portabilidade leva ao rito (/servidor/portabilidade) com o banco escolhido.
  //
  // Cliente pediu 28/07/2026: mostrar TODAS as ofertas nesta tela (nao so
  // portabilidade/refin) — as ofertas de credito novo e cartao consignado
  // que o banco publica no /banco/ofertas passam a aparecer aqui acima do
  // card "Solicitar portabilidade". Cada oferta mantem CTA distinto por
  // tipo (portabilidade -> /portabilidade, credito novo -> /termo, cartao
  // -> /solicitar-cartao) via tipoLabelHref — o bug antigo em que clicar
  // em "credito novo" iniciava portabilidade e' evitado pelo CTA proprio.
  const ofertas = (ofertasQ.data?.ofertas ?? []).filter(
    (o) => o.tipo !== "cartao_beneficio",
  );

  // Agrupa em secoes separadas por tipo de produto ainda disponivel.
  const ofertasPorTipo = useMemo(() => {
    const g: Record<string, typeof ofertas> = { portabilidade: [], refinanciamento: [], credito_novo: [], cartao_consignado: [] };
    for (const o of ofertas) {
      const k = o.tipo ?? "credito_novo";
      if (g[k]) g[k]!.push(o);
    }
    return g;
  }, [ofertas]);
  const SECOES: { key: keyof typeof ofertasPorTipo; titulo: string; subtitulo: string }[] = [
    { key: "refinanciamento", titulo: "Refinanciamento", subtitulo: "Renegocie um contrato existente — pode alongar prazo ou pegar troco." },
    { key: "credito_novo", titulo: "Crédito novo", subtitulo: "Ofertas de empréstimo consignado das instituições parceiras." },
    { key: "cartao_consignado", titulo: "Cartão consignado", subtitulo: "Cartão com fatura mínima descontada em folha (margem cartão)." },
  ];

  if (!info) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24, maxWidth: 1080, width: "100%", margin: "0 auto" }}>
      {/* Header */}
      <header>
        <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
          Portal do servidor
        </span>
        <h1 style={{ margin: "4px 0 0", fontSize: "1.8rem", letterSpacing: "-0.02em" }}>Ofertas e portabilidade</h1>
        <p style={{ color: "var(--text-muted)", margin: "6px 0 0" }}>
          Ofertas das instituições parceiras para o convênio da <b>{info.prefeitura}</b> — crédito novo, cartão consignado, refinanciamento. Solicite portabilidade no card abaixo.
        </p>
      </header>

      {/* 1. OFERTAS DOS BANCOS PARCEIROS (primeira secao — cards com tabelas).
          Se nao ha oferta, a secao inteira some (nao mostra mais o placeholder
          "Nenhuma oferta ativa"). Cliente pediu — as demais secoes (Rede de
          saude, Solicitar portabilidade) preenchem a tela mesmo sem ofertas. */}
      {ofertasQ.isLoading ? (
        <div style={{ color: "var(--text-muted)", fontSize: 14 }}>Carregando ofertas…</div>
      ) : ofertasQ.error ? (
        <div style={{ color: "var(--danger-500)", fontSize: 14 }}>Falha ao carregar ofertas.</div>
      ) : ofertas.length === 0 ? null : (
        <>
          {SECOES.map((sec) => {
            const lista = ofertasPorTipo[sec.key] ?? [];
            if (lista.length === 0) return null;
            return (
              <section key={sec.key} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div>
                  <div style={{ fontSize: 11, letterSpacing: "0.08em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
                    {sec.titulo}
                  </div>
                  <p style={{ fontSize: 13, color: "var(--text-muted)", margin: "2px 0 0" }}>{sec.subtitulo}</p>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
                  {lista.map((o) => {
                    const valorSug = Math.min(o.valorMax, 10000);
                    const parcelasSug = Math.min(o.parcelasMax, 60);
                    // CTA por tipo (mesmo mapping do submenu do banco):
                    //  credito_novo    → /servidor/termo (fluxo de aceite ja existente)
                    //  portabilidade   → /servidor/portabilidade com este banco pre-selecionado
                    //  refinanciamento → /servidor/portabilidade em modo refin
                    //  cartao_*        → /servidor/solicitar-cartao
                    const tipoMeta = tipoLabelHref(o, valorSug, parcelasSug);
                    return (
                      <Card key={o.id}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: "var(--accent)", textTransform: "uppercase" }}>
                            {o.bancoNome}
                          </div>
                          <span style={{ fontSize: 10, letterSpacing: "0.06em", fontWeight: 700, color: tipoMeta.cor }}>
                            {tipoMeta.label}
                          </span>
                        </div>
                        <h3 style={{ margin: "6px 0", fontSize: "1.1rem", display: "flex", alignItems: "center", gap: 8 }}>
                          {o.icone ? <span style={{ fontSize: "1.3rem" }}>{o.icone}</span> : null}
                          <span>{o.titulo}</span>
                        </h3>
                        <p style={{ color: "var(--text-muted)", margin: "4px 0", fontSize: 14 }}>
                          {o.mensagem}
                        </p>
                        <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <span style={chip}>{pct(o.taxaAm)}</span>
                          <span style={chip}>Até {o.parcelasMax}×</span>
                          <span style={chip}>Até {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 }).format(o.valorMax)}</span>
                        </div>
                        <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                          <Button size="sm" variant="ghost" onClick={() => nav(tipoMeta.href)}>
                            {tipoMeta.cta}
                          </Button>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </>
      )}

      {/* Secao "Rede de saude parceira" foi removida a pedido do cliente
          (15/07/2026). Os beneficios de saude vivem exclusivamente na aba
          /servidor/beneficios agora. */}

      {/* Botao de PORTABILIDADE (destaque medio) */}
      <AcaoCard
        icone="🔁"
        titulo="Levar um contrato para outra instituição"
        descricao="Escolha o contrato e a instituição de destino. A instituição atual informa o saldo, a nova paga e o contrato muda de instituição."
        cor="var(--gold-500)"
        onClick={() => nav("/servidor/portabilidade")}
      />

      {/* Cliente pediu que o Marketplace mostre SO Ofertas + Solicitar portabilidade.
          Simulador (era secao 3) foi removido — servidor simula pelos botoes "Simular"
          dos cards de margem no dashboard, nao aqui.
          Propostas de portabilidade dos bancos (era secao 4) tambem foi removida por
          pedido explicito ("so quero que apareca (Ofertas para voce e Solicitar Portabilidade)").
          As propostas seguem chegando via /servidor/portabilidade. */}
    </div>
  );
}

// Componentes FeaturedComTrocoCard/SimplesCard/SubCard/TabBtn/pillStyle
// removidos 27/07/2026 — eram codigo morto desde que o cliente pediu que o
// Marketplace mostrasse SO "Ofertas para voce" + "Solicitar portabilidade"
// (comentario L253-258). Continham taxas ficticias hardcoded (2.29%/2.15%
// e parcela +R$37.50) que renderizavam valores falsos se o codigo voltasse
// a ser usado. Auditoria de UX 27/07/2026.

/** Card grande de acao — usado agora so pra "Solicitar portabilidade". */
function AcaoCard({
  icone, titulo, descricao, cor, onClick,
}: {
  icone: string; titulo: string; descricao: string; cor: string; onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: "left",
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 14,
        padding: 18,
        display: "flex",
        gap: 14,
        alignItems: "flex-start",
        cursor: "pointer",
        transition: "border-color .12s ease, transform .12s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = cor;
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "var(--border)";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      <div style={{
        width: 44, height: 44, borderRadius: 12,
        background: `color-mix(in srgb, ${cor} 15%, transparent)`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 22, flexShrink: 0,
      }}>{icone}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>{titulo}</div>
        <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4, lineHeight: 1.5 }}>
          {descricao}
        </div>
      </div>
      <span style={{ fontSize: 22, color: cor, alignSelf: "center" }}>→</span>
    </button>
  );
}

const chip: React.CSSProperties = {
  padding: "4px 10px",
  borderRadius: 999,
  background: "var(--bg-elev-2)",
  color: "var(--text-muted)",
  fontSize: 11,
  fontWeight: 600,
};
